import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback } from 'react';

import { packages } from "../../../env";
import Button from "../../common/Button";
import { getRequest, postRequest } from '../../../config';
import ModalWrapper from '../../common/Modal/ModalWrapper';
import { toggleModal } from '../../../reducers/modalReducer';

import debounce from 'lodash/debounce';
import { stepConfig } from './constants';
import PurchaseInfo from './PurchaseInfo';
import ActionButtons from './ActionButtons';
import PromoCodeInput from './PromoCodeInput';

const SubscriptionPaymentConfirm = ({ plan, companyId, defaultStep, setShowExpension, payload, setModuleTab, module, navigateTo, onCancel }) => {
    const dispatch = useDispatch();
    const modalState = useSelector(state => state.modal);
    const { open } = modalState;

    const effectivePlan = modalState.purchasePlan || plan;
    const effectiveModule = modalState.modulePurchase || module;
    const effectiveDefaultStep = modalState.purchaseDefaultStep || defaultStep;

    const [loading, setLoading] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [promoCodeData, setPromoCodeData] = useState(null);
    const [isValidCoupon, setIsValidCoupon] = useState(true);
    const [isCheckingCoupon, setIsCheckingCoupon] = useState(false);
    const [step, setStep] = useState(defaultStep ?? "subscriptionUpgrade");

    const resolveCouponAmount = useCallback(() => {
        if (effectivePlan === 'tempModulePurchase' && effectiveModule?.price != null) {
            const n = parseFloat(String(effectiveModule.price).replace(/[^0-9.]/g, ''));
            return Number.isFinite(n) ? n : packages[plan]?.price;
        }
        return packages[plan]?.price;
    }, [effectivePlan, effectiveModule, plan]);

    const checkCoupon = useCallback(
        debounce(async (code) => {
            if (!code) {
                setIsValidCoupon(true);
                setIsCheckingCoupon(false);
                return;
            }

            try {
                const response = await postRequest(`/payment/checkCoupon/${code}`, { amount: resolveCouponAmount() });
                setPromoCodeData(response?.body);
                setIsValidCoupon(response?.code === 200);
            } catch (error) {
                setIsValidCoupon(false);
            } finally {
                setIsCheckingCoupon(false);
            }
        }, 1000),
        [plan, resolveCouponAmount]
    );

    useEffect(() => {
        setIsCheckingCoupon(true);
        checkCoupon(promoCode);
    }, [promoCode, checkCoupon]);

    useEffect(() => {
        const stepDefault = modalState.purchaseDefaultStep || defaultStep;
        if (stepDefault) {
            setStep(stepDefault);
        } else {
            if (effectivePlan === "Free") {
                setStep("freeMembership");
            } else if (effectivePlan === "modulePurchase") {
                setStep("modulePurchase");
            } else if (effectivePlan === "tempModulePurchase") {
                setStep("tempModulePurchase");
            } else if (typeof effectivePlan === 'string' && (effectivePlan.startsWith('two-minute-') || effectivePlan.startsWith('investor-lead'))) {
                setStep("freeModule");
            } else {
                setStep("subscriptionUpgrade");
            }
        }
    }, [plan, open, effectivePlan, defaultStep, modalState.purchaseDefaultStep]);

    const handleClose = () => {
        setPromoCodeData(null);
        setPromoCode("");
        dispatch(toggleModal({
            open: '',
            modulePurchase: undefined,
            purchasePlan: undefined,
            purchaseDefaultStep: undefined,
        }));
        setStep("subscriptionUpgrade");
        window.history.replaceState(null, null, '');
        if (setShowExpension) {
            setShowExpension(null)
        }
        if (onCancel) onCancel()
    };

    const handlePurchase = async () => {
        const isTempCompany = location.pathname.includes(`/company-new/`);
        setLoading(true);
        if (effectivePlan === "Free") {
            const response = await getRequest(`/payment/freeSubscription/${companyId}`);
            if (response?.code === 200) {
                if (navigateTo) {
                    dispatch(toggleModal({ open: '' }));
                } else {
                    setTimeout(() => location.href = `/company/${companyId}`, 1000);
                }
            }
        } else {
            const data = {
                endPoint: `/payment/upgrade-subscription/${companyId}`,
                payload: { packageName: effectivePlan, ...(promoCodeData && { promoCode: promoCodeData.stripePromotionCodeId }) }
            };

            if (['Free', 'Lite', 'Pro'].includes(effectivePlan) && promoCodeData && promoCodeData?.discountType === "percentage" && promoCodeData?.discountValue === 100) {
                data.endPoint = `/payment/subscriptionWithAccessCode/${companyId}`;
                data.payload = { packageName: effectivePlan, promoCode: promoCodeData.code };
            }

            if (effectivePlan === 'tempModulePurchase' || effectiveDefaultStep === 'tempModulePurchase') {
                data.endPoint = `/modules/purchase-temp-module/${companyId}`;
                data.payload = {
                    tempModuleId: effectiveModule?.tempModuleId,
                    ...(promoCodeData && { promoCode: promoCodeData.stripePromotionCodeId }),
                };
                if (promoCodeData && promoCodeData?.discountType === "percentage" && promoCodeData?.discountValue === 100) {
                    data.endPoint = `/modules/purchase-temp-module-with-access-code/${companyId}`;
                    data.payload = { tempModuleId: effectiveModule?.tempModuleId, promoCode: promoCodeData.code };
                }
            } else if (effectiveDefaultStep === "modulePurchase") {
                data.endPoint = `/payment/purchase-expansion/${companyId}`;
                data.payload = { expansionName: effectivePlan, ...(promoCodeData && { promoCode: promoCodeData.stripePromotionCodeId }) };

                if (promoCodeData && promoCodeData?.discountType === "percentage" && promoCodeData?.discountValue === 100) {
                    data.endPoint = `/payment/addExpensionPackWithAccessCode/${companyId}`;
                    data.payload = { expansionName: effectivePlan, promoCode: promoCodeData.code };
                }
            }
            const response = await postRequest(`${data?.endPoint}`, data?.payload);
            if (response?.code === 200) {
                if (response.body?.url) {
                    window.location.href = response.body.url;
                } else {
                    window.location.href = isTempCompany ? `/company-new/${companyId}` : `/company/${companyId}`;
                }
                if (setShowExpension) {
                    setShowExpension(null);
                }
                dispatch(toggleModal({
                    open: '',
                    modulePurchase: undefined,
                    purchasePlan: undefined,
                    purchaseDefaultStep: undefined,
                }));
                setPromoCode("");
            }
        }
        setLoading(false);
    };

    const { title, subtitle } = stepConfig[step] ?? {};
    return (
        <ModalWrapper onCloseHandler={handleClose} modalType='subscriptionPaymentConfirm'>
            <div className='df fdc jcc aic'>
                <img
                    style={{ width: '93px', height: '75px' }}
                    src={require('../../../../images/checklist-logo.webp')}
                    alt='logoDiligentsia'
                />

                <h3 className='c15 tac mt1rem fw400 fs22px'>{title}</h3>

                {navigateTo && step === "discountCode" ? null : <p className='c15 op60 fs10px my0-5rem tac fw500'>{subtitle}</p>}


                {step === "subscriptionUpgrade" && (
                    <>
                        <PurchaseInfo text={<>You are buying the {effectivePlan} subscription at<br />£{Number(packages[effectivePlan]?.price / 12).toFixed(2)} <span class='fs0-75rem c14 fw300'>(plus VAT)</span> per month for 12 months</>} />
                        <ActionButtons disabled={loading} plan={effectivePlan} onCancel={handleClose} onConfirm={() => setStep("discountCode")} />
                    </>
                )}

                {step === "discountCode" && (
                    <>
                        <PromoCodeInput
                            promoCode={promoCode}
                            setPromoCode={setPromoCode}
                            isValid={isValidCoupon}
                            isChecking={isCheckingCoupon}
                        />
                        <div className='df fdc aic jcc w100 mt3rem mb1rem' style={{ width: '280px' }}>
                            <Button
                                onClick={handlePurchase}
                                btnType="warning"
                                customClassName='w100'
                                text="Continue to Purchase"
                                disabled={loading || isCheckingCoupon || (!isValidCoupon && promoCode !== '')}
                            />
                        </div>
                    </>
                )}

                {step === "freeMembership" && (
                    <>
                        <PurchaseInfo text={
                            <>The <span className='c59 op70 ttu'>{plan}</span> subscription offers a great deal more and is <br /> only £{packages[plan]?.price} per year. No credit card required. Later you can upgrade to Lite or Pro from within your dashboard.</>
                        } />
                        <ActionButtons disabled={loading} plan={plan} step={step} onCancel={handleClose} onConfirm={handlePurchase} confirmText="Subscribe for Free" />
                    </>
                )}

                {step === "modulePurchase" ? (
                    <>
                        <PurchaseInfo text={
                            <>The module is {module && typeof module !== "string" ? module['type'] : packages[plan]?.name} <br />The module price is {module && typeof module !== "string" ? module['price'] : `£${packages[plan]?.price}`} <span class='fs0-75rem c14 fw300'>(plus VAT)</span></>
                        } />
                        <ActionButtons disabled={loading} plan={defaultStep} onCancel={handleClose} onConfirm={() => setStep("discountCode")} />
                    </>
                ) : <></>}

                {step === "tempModulePurchase" ? (
                    <>
                        <PurchaseInfo text={
                            <>
                                The module is {effectiveModule?.moduleName || '—'} <br />
                                {effectiveModule?.subscriptionTier != null && (
                                    <>Subscription: {String(effectiveModule.subscriptionTier)} · Objective: {String(effectiveModule.companyObjective || '—')}<br /></>
                                )}
                                The module price is {effectiveModule?.price != null ? effectiveModule.price : '—'} <span className='fs0-75rem c14 fw300'>(plus VAT)</span>
                            </>
                        } />
                        <ActionButtons disabled={loading} plan={effectiveDefaultStep} onCancel={handleClose} onConfirm={() => setStep("discountCode")} />
                    </>
                ) : <></>}

                {step === "freeModule" && (
                    <>
                        <PurchaseInfo text={
                            <>The module is {payload.module.heading} <br />This module is free to use</>
                        } />
                        <ActionButtons
                            disabled={loading}
                            plan={plan}
                            onCancel={handleClose}
                            onConfirm={() => {
                                setModuleTab && setModuleTab(payload.setTab, payload.type, payload.module);
                            }}
                            confirmText="Start Module"
                        />
                    </>
                )}
            </div>
        </ModalWrapper>
    );
};

export default SubscriptionPaymentConfirm;
